const Branzos = {
  init() {
    this.initHeader();
    this.initMobileMenu();
    this.initDrawers();
    this.initToasts();
    this.initReveal();
    this.initBackToTop();
    this.initAccordions();
    this.initCarousels();
    this.initQtySelectors();
    this.initWishlist();
    this.initGlobalCartButtons();
    this.initShare();
    this.initFilterGroups();
  },

  initFilterGroups() {
    document.addEventListener('click', (e) => {
      const title = e.target.closest('.filter-group__title');
      if (!title) return;
      const group = title.closest('.filter-group');
      group.classList.toggle('is-collapsed');
      title.setAttribute('aria-expanded', String(!group.classList.contains('is-collapsed')));
    });
  },

  initShare() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-share-copy]');
      if (!btn) return;
      const url = btn.dataset.shareUrl || window.location.href;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(() => {
          this.showToast('Link copied to clipboard', '🔗');
        }).catch(() => {
          this.fallbackCopy(url);
        });
      } else {
        this.fallbackCopy(url);
      }
    });
  },

  fallbackCopy(text) {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      this.showToast('Link copied to clipboard', '🔗');
    } catch {
      this.showToast('Could not copy link', '😵');
    }
    document.body.removeChild(ta);
  },

  debounce(fn, wait = 300) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  },

  onLoad(cb) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', cb);
    } else {
      cb();
    }
  },

  initHeader() {
    const header = document.querySelector('.header');
    if (!header) return;
    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  },

  initMobileMenu() {
    const panel = document.querySelector('[data-mobile-menu]');
    const toggle = document.querySelector('[data-menu-toggle]');
    const close = document.querySelector('[data-menu-close]');
    const overlay = document.querySelector('[data-menu-overlay]');
    if (!panel || !toggle) return;

    const open = () => {
      panel.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    };
    const closePanel = () => {
      panel.classList.remove('is-open');
      document.body.style.overflow = '';
    };

    toggle.addEventListener('click', open);
    if (close) close.addEventListener('click', closePanel);
    if (overlay) overlay.addEventListener('click', closePanel);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closePanel();
    });

    panel.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', closePanel);
    });
  },

  initDrawers() {
    document.querySelectorAll('[data-drawer-toggle]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const target = document.querySelector(btn.dataset.drawerToggle);
        if (!target) return;
        if (target.classList.contains('is-open')) {
          this.closeDrawer(target);
        } else {
          this.openDrawer(target);
        }
      });
    });

    document.querySelectorAll('[data-drawer-close]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const target = btn.dataset.drawerClose
          ? document.querySelector(btn.dataset.drawerClose)
          : btn.closest('.drawer, .details-modal, .quick-view-modal, .mobile-menu, .filter-drawer');
        if (target) this.closeDrawer(target);
      });
    });

    document.querySelectorAll('[data-drawer-overlay]').forEach((overlay) => {
      overlay.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeDrawer(overlay.closest('.drawer, .details-modal, .quick-view-modal, .mobile-menu, .filter-drawer'));
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        document.querySelectorAll('.is-open').forEach((el) => {
          if (el.classList.contains('drawer') || el.classList.contains('details-modal') ||
              el.classList.contains('quick-view-modal') || el.classList.contains('mobile-menu') ||
              el.classList.contains('filter-drawer')) {
            this.closeDrawer(el);
          }
        });
      }
    });
  },

  openDrawer(el) {
    if (!el) return;
    el.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    const focus = el.querySelector('[data-autofocus]');
    if (focus) setTimeout(() => focus.focus(), 350);
  },

  closeDrawer(el) {
    if (!el) return;
    el.classList.remove('is-open');
    if (!document.querySelector('.drawer.is-open, .details-modal.is-open, .quick-view-modal.is-open, .mobile-menu.is-open, .filter-drawer.is-open')) {
      document.body.style.overflow = '';
    }
  },

  initToasts() {
    this.toastEl = document.querySelector('[data-toast]');
    this.toastEmoji = this.toastEl ? this.toastEl.querySelector('[data-toast-emoji]') : null;
    this.toastText = this.toastEl ? this.toastEl.querySelector('[data-toast-text]') : null;
    this.toastTimer = null;
  },

  showToast(message, emoji = '🍬') {
    if (!this.toastEl || !this.toastText) return;
    this.toastText.textContent = message;
    if (this.toastEmoji) this.toastEmoji.textContent = emoji;
    this.toastEl.classList.add('is-visible');
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      this.toastEl.classList.remove('is-visible');
    }, 3000);
  },

  initReveal() {
    const els = document.querySelectorAll('.reveal');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    els.forEach((el) => observer.observe(el));
  },

  initBackToTop() {
    const btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;
    window.addEventListener('scroll', () => {
      btn.classList.toggle('is-visible', window.scrollY > 600);
    }, { passive: true });
    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  },

  initAccordions() {
    document.querySelectorAll('[data-accordion]').forEach((wrap) => {
      wrap.querySelectorAll('.accordion__header').forEach((header) => {
        header.addEventListener('click', () => {
          const item = header.closest('.accordion__item');
          const content = item.querySelector('.accordion__content');
          const isOpen = item.classList.contains('is-open');
          const all = wrap.querySelectorAll('.accordion__item');
          all.forEach((other) => {
            if (other !== item && other.classList.contains('is-open')) {
              other.classList.remove('is-open');
              other.querySelector('.accordion__content').style.maxHeight = null;
            }
          });
          item.classList.toggle('is-open', !isOpen);
          if (!isOpen) {
            content.style.maxHeight = content.scrollHeight + 'px';
          } else {
            content.style.maxHeight = null;
          }
        });
      });
    });

    document.querySelectorAll('[data-faq]').forEach((wrap) => {
      wrap.querySelectorAll('.faq__question').forEach((q) => {
        q.addEventListener('click', () => {
          const item = q.closest('.faq__item');
          const answer = item.querySelector('.faq__answer');
          const isOpen = item.classList.contains('is-open');
          item.classList.toggle('is-open', !isOpen);
          if (!isOpen) {
            answer.style.maxHeight = answer.scrollHeight + 'px';
          } else {
            answer.style.maxHeight = null;
          }
        });
      });
    });
  },

  initCarousels() {
    document.querySelectorAll('[data-carousel]').forEach((carousel) => {
      const track = carousel.querySelector('[data-carousel-track]');
      if (!track) return;
      const prev = carousel.querySelector('[data-carousel-prev]');
      const next = carousel.querySelector('[data-carousel-next]');
      let index = 0;

      const getItemsPerView = () => {
        if (window.innerWidth <= 589) return 1;
        if (window.innerWidth <= 989) return 2;
        return 4;
      };

      const getItemWidth = () => {
        const item = track.querySelector('.carousel__item');
        if (!item) return 0;
        return item.offsetWidth;
      };

      const getGap = () => {
        const style = getComputedStyle(track);
        return parseFloat(style.columnGap) || parseFloat(style.gap) || 0;
      };

      const moveTo = (i) => {
        const total = track.children.length;
        const perView = getItemsPerView();
        const maxIndex = Math.max(0, total - perView);
        index = Math.max(0, Math.min(i, maxIndex));
        if (window.innerWidth <= 589) {
          track.style.transform = '';
          if (prev) prev.style.visibility = 'hidden';
          if (next) next.style.visibility = 'hidden';
          return;
        }
        const itemWidth = getItemWidth();
        const gap = getGap();
        track.style.transform = `translateX(-${index * (itemWidth + gap)}px)`;
        if (prev) prev.style.visibility = index > 0 ? 'visible' : 'hidden';
        if (next) next.style.visibility = index < maxIndex ? 'visible' : 'hidden';
      };

      if (prev) prev.addEventListener('click', () => moveTo(index - 1));
      if (next) next.addEventListener('click', () => moveTo(index + 1));

      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => moveTo(index), 200);
      });
      moveTo(0);
    });
  },

  initQtySelectors() {
    document.querySelectorAll('[data-qty-wrap]').forEach((wrap) => {
      if (wrap.dataset.qtyBound) return;
      wrap.dataset.qtyBound = '1';
      const input = wrap.querySelector('[data-qty-input]');
      const minus = wrap.querySelector('[data-qty-minus]');
      const plus = wrap.querySelector('[data-qty-plus]');
      if (!input) return;

      const set = (val) => {
        const max = parseInt(input.dataset.max || '99', 10);
        const next = Math.max(1, Math.min(val, max));
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      if (minus) minus.addEventListener('click', () => set(parseInt(input.value || '1', 10) - 1));
      if (plus) plus.addEventListener('click', () => set(parseInt(input.value || '1', 10) + 1));
      input.addEventListener('change', () => {
        const val = parseInt(input.value || '1', 10);
        set(Number.isNaN(val) ? 1 : val);
      });
    });
  },

  initWishlist() {
    this.wishlistKey = 'branzos_wishlist';
    this.wishlistEnabled = window.ShopifyTheme && window.ShopifyTheme.wishlistEnabled;

    const get = () => {
      try {
        return JSON.parse(localStorage.getItem(this.wishlistKey) || '[]');
      } catch {
        return [];
      }
    };

    const save = (items) => {
      localStorage.setItem(this.wishlistKey, JSON.stringify(items));
    };

    const syncUI = () => {
      const items = get();
      document.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
        const handle = btn.dataset.wishlistHandle;
        btn.classList.toggle('is-active', items.includes(handle));
        btn.setAttribute('aria-pressed', String(items.includes(handle)));
        const label = btn.querySelector('[data-wishlist-label]');
        if (label) label.textContent = items.includes(handle) ? window.ShopifyTheme.i18n.inWishlist : window.ShopifyTheme.i18n.addWishlist;
      });
      document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
        el.textContent = items.length;
      });
    };

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-btn]');
      if (!btn) return;
      e.preventDefault();
      const handle = btn.dataset.wishlistHandle;
      const items = get();
      const idx = items.indexOf(handle);
      let added = true;
      if (idx >= 0) {
        items.splice(idx, 1);
        added = false;
      } else {
        items.push(handle);
      }
      save(items);
      syncUI();
      this.showToast(added ? window.ShopifyTheme.i18n.addedWishlist : window.ShopifyTheme.i18n.removedWishlist, added ? '💖' : '💔');
    });

    syncUI();
  },

  initGlobalCartButtons() {
    document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const drawer = document.querySelector('[data-cart-drawer]');
        if (drawer) {
          if (typeof window.ShopifyCart !== 'undefined') {
            window.ShopifyCart.refresh();
          }
          this.openDrawer(drawer);
        }
      });
    });

    document.addEventListener('click', (e) => {
      const quickAdd = e.target.closest('[data-quick-add]');
      if (!quickAdd) return;
      e.preventDefault();
      if (typeof window.ShopifyCart !== 'undefined') {
        window.ShopifyCart.add(quickAdd.dataset.variantId, 1, quickAdd);
      }
    });
  },
};

Branzos.onLoad(() => Branzos.init());
